import { gql, useLazyQuery } from '@apollo/client';
import { ExportFileProgressIndicator } from '@modules/common/components/ExportFileProgressIndicator';
import {
  ToastNotificationType,
  useNotificationContext,
} from '@modules/common/components/Notification/NotificationContext';
import { downloadFileFromURL } from '@modules/common/utils';
import { DownloadForOfflineRounded } from '@mui/icons-material';
import { ExportEqlAsCsvInput, Query } from 'generated/graphql';
import { useCallback } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { useGetUser } from 'v2/core/services/user';

const EXPORT_ANALYSIS_AS_CSV_QUERY = gql`
  query exportEQLAsCSV(
    $tenantID: ID!
    $expression: String!
    $filename: String!
    $columns: [ExportCSVColumn!]!
  ) {
    exportEQLAsCSV(
      request: {
        tenantID: $tenantID
        expression: $expression
        filename: $filename
        columns: $columns
      }
    ) {
      url
    }
  }
`;

export const useExportAnalysisAsCSV = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const {
    showNotification,
    hideNotification,
    showErrorNotification,
    showSuccessNotification,
  } = useNotificationContext();

  const [
    exportAnalysisAsCSVQuery,
    { data, error, loading, called, networkStatus },
  ] = useLazyQuery<Query, ExportEqlAsCsvInput>(EXPORT_ANALYSIS_AS_CSV_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to export CSV.',
        props: {},
      });
    },
    onCompleted: (data) => {
      if (!data.exportEQLAsCSV) {
        return;
      }
      downloadFileFromURL(data.exportEQLAsCSV.url);
      showSuccessNotification({
        contentComponent: 'CSV exported successfully.',
        props: {},
      });
    },
  });

  const exportAnalysisAsCSV = useCallback(
    async ({
      expression,
      filename,
      columns,
    }: Pick<ExportEqlAsCsvInput, 'expression' | 'filename' | 'columns'>) => {
      const notificationID = showNotification({
        contentComponent: () => (
          <ExportFileProgressIndicator fileType="CSV" eta="5 secs" />
        ),
        props: {
          isClosable: false,
          autoClose: false,
          notificationType: ToastNotificationType.InfoCard,
          severity: 'info',
          leftOffset: '98px',
          icon: <DownloadForOfflineRounded sx={{ alignSelf: 'center' }} />,
        },
      });
      try {
        await exportAnalysisAsCSVQuery({
          variables: {
            tenantID,
            expression,
            filename,
            columns,
          },
        });
      } catch {
        // do nothing, error is handled by onError
      }
      hideNotification(notificationID);
    },
    [exportAnalysisAsCSVQuery, hideNotification, showNotification, tenantID],
  );

  const status = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    error,
    networkStatus,
  );

  return [
    exportAnalysisAsCSV,
    {
      data,
      error,
      status,
      called,
      loading,
    },
  ] as const;
};
